import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { MaintenanceThresholdRecord, MaintenanceThreshold2 } from '../services/maintenanceService';
import { fetchMaintRegistry, fetchMaintenanceThreshold, fetchMaintenanceTickets } from '../services/maintenanceService';
import type { MaintRegisrty, TicketHistory } from '../types/dashboardTypes';
import isEqual from 'lodash/isEqual';

/**
 * MaintenanceSlice - Handles all maintenance-related state
 * Including maintenance history, tickets, and threshold data
 */
interface MaintenanceState {
    loadingMaintenanceHistory: boolean;
    loadingMaintenanceTicketsGlobal: boolean;
    loadingMaintenanceTicketsSite: boolean;
    loadingMaintenance: boolean;
    maintenanceHistory: MaintRegisrty[];
    maintenanceTicketGlobal: TicketHistory[]; // Customer-level tickets
    maintenanceTicketSite: TicketHistory[]; // Site-level tickets
    maintenanceData: MaintenanceThresholdRecord[];
    maintenanceResponses: MaintenanceThreshold2[];
    maintenances: MaintenanceThresholdRecord[];
    errorMaintenance: string | null;
    errorMaintenanceHistory: string | null;
    errorMaintenanceTicketGlobal: string | null;
    errorMaintenanceTicketSite: string | null;
}

const initialState: MaintenanceState = {
    loadingMaintenanceHistory: false,
    loadingMaintenanceTicketsGlobal: false,
    loadingMaintenanceTicketsSite: false,
    loadingMaintenance: false,
    maintenanceHistory: [],
    maintenanceTicketGlobal: [],
    maintenanceTicketSite: [],
    maintenanceData: [],
    maintenanceResponses: [],
    maintenances: [],
    errorMaintenance: null,
    errorMaintenanceHistory: null,
    errorMaintenanceTicketGlobal: null,
    errorMaintenanceTicketSite: null,
};

const maintenanceSlice = createSlice({
    name: 'maintenance',
    initialState,
    reducers: {
        clearMaintenanceData(state) {
            state.maintenanceData = [];
            state.maintenanceHistory = [];
            state.maintenanceResponses = [];
            state.maintenanceTicketGlobal = [];
            state.maintenanceTicketSite = [];
            state.maintenances = [];
            state.errorMaintenance = null;
            state.errorMaintenanceHistory = null;
            state.errorMaintenanceTicketGlobal = null;
            state.errorMaintenanceTicketSite = null;
        },
        clearSiteLevelMaintenanceData(state) {
            // Only clear site-level data, keep global data intact
            state.maintenanceTicketSite = [];
            state.errorMaintenanceTicketSite = null;
            state.loadingMaintenanceTicketsSite = false;
        },
        updateMaintenanceThreshold(
            state,
            action: PayloadAction<{ data: MaintenanceThresholdRecord[]; customer: string }>
        ) {
            // Find if maintenance data for this customer already exists
            const existingIndex = state.maintenanceResponses.findIndex(
                (entry: any) => entry.customer === action.payload.customer
            );

            if (existingIndex !== -1) {
                // Update existing entry if data has changed
                if (!isEqual(state.maintenanceResponses[existingIndex].data, action.payload.data)) {
                    state.maintenanceResponses[existingIndex].data = action.payload.data;
                }
            } else {
                // Add new customer maintenance data
                state.maintenanceResponses.push({
                    customer: action.payload.customer,
                    data: action.payload.data,
                });
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMaintRegistry.pending, (state) => {
                // Always show loading for manual refreshes
                state.loadingMaintenanceHistory = true;
                state.errorMaintenanceHistory = null;
            })
            .addCase(fetchMaintRegistry.fulfilled, (state, action: PayloadAction<MaintRegisrty[]>) => {
                state.loadingMaintenanceHistory = false;
                state.errorMaintenanceHistory = null;
                state.maintenanceHistory = action.payload;
            })
            .addCase(fetchMaintRegistry.rejected, (state, action) => {
                state.loadingMaintenanceHistory = false;
                state.errorMaintenanceHistory = action.payload as string;
            })
            .addCase(fetchMaintenanceTickets.pending, (state, action) => {
                const isGlobal = action.meta.arg.selectedSite === undefined;

                if (isGlobal) {
                    // Always show loading for manual refreshes
                    state.loadingMaintenanceTicketsGlobal = true;
                } else {
                    // Always show loading for manual refreshes
                    state.loadingMaintenanceTicketsSite = true;
                }
            })
            .addCase(fetchMaintenanceTickets.fulfilled, (state, action) => {
                const isGlobal = action.meta.arg.selectedSite === undefined;

                if (isGlobal) {
                    // Store in global state
                    state.loadingMaintenanceTicketsGlobal = false;
                    state.maintenanceTicketGlobal = action.payload as any;
                    state.errorMaintenanceTicketGlobal = null;
                } else {
                    // Store in site state
                    state.loadingMaintenanceTicketsSite = false;
                    state.maintenanceTicketSite = action.payload as any;
                    state.errorMaintenanceTicketSite = null;
                }
            })
            .addCase(fetchMaintenanceTickets.rejected, (state, action) => {
                const isGlobal = action.meta.arg.selectedSite === undefined;

                // Always set loading to false on rejection, regardless of cancellation
                // If request was canceled or failed, user should be able to retry
                if (isGlobal) {
                    state.loadingMaintenanceTicketsGlobal = false;
                    if (action.payload && action.payload !== 'Request canceled') {
                        state.errorMaintenanceTicketGlobal = action.payload as string;
                    }
                } else {
                    state.loadingMaintenanceTicketsSite = false;
                    if (action.payload && action.payload !== 'Request canceled') {
                        state.errorMaintenanceTicketSite = action.payload as string;
                    }
                }
            })
            .addCase(fetchMaintenanceThreshold.pending, (state) => {
                // Always show loading for manual refreshes
                state.loadingMaintenance = true;
            })
            .addCase(fetchMaintenanceThreshold.fulfilled, (state, action: PayloadAction<any>) => {
                state.loadingMaintenance = false;
                state.maintenanceData = action.payload;
            })
            .addCase(fetchMaintenanceThreshold.rejected, (state, action) => {
                state.loadingMaintenance = false;
                state.errorMaintenance = action.payload as string;
            });
    },
});

export const { clearMaintenanceData, clearSiteLevelMaintenanceData, updateMaintenanceThreshold } = maintenanceSlice.actions;
export default maintenanceSlice.reducer;
